/**
 * Исходник `AudioWorkletProcessor`, который снимает сырой PCM с микрофона.
 *
 * Модуль ворклета обязан загружаться отдельным файлом через
 * `audioWorklet.addModule(url)` — он исполняется в отдельном потоке
 * рендеринга звука и не видит модули приложения. Отдельный файл в `public/`
 * ломался бы при смене базового пути сборки, поэтому исходник лежит здесь
 * строкой, а URL создаётся из `Blob` на лету и сразу освобождается.
 *
 * Процессор ничего не пишет в выход и ничего не накапливает: каждый кадр
 * (128 отсчётов, ~8 мс при 16 кГц) уходит в основной поток как есть.
 * Накопление и кодирование — задача `AudioRecorder`.
 */

/** Имя, под которым процессор регистрируется и создаётся `AudioWorkletNode`. */
export const RECORDER_WORKLET_NAME = 'pcm-recorder-processor';

const WORKLET_MIME_TYPE = 'application/javascript';

/** Исходник процессора. Исполняется в AudioWorkletGlobalScope. */
export const RECORDER_WORKLET_SOURCE = `
class PcmRecorderProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const input = inputs[0];
    if (!input || input.length === 0) return true;
    const channel = input[0];
    if (!channel || channel.length === 0) return true;
    // Движок переиспользует буфер кадра между вызовами — отдаём копию,
    // иначе следующий кадр затрёт уже отправленные данные.
    const frame = new Float32Array(channel);
    this.port.postMessage(frame, [frame.buffer]);
    // true — узел остаётся живым, пока его не отключат из основного потока.
    return true;
  }
}

registerProcessor('${RECORDER_WORKLET_NAME}', PcmRecorderProcessor);
`;

/**
 * Регистрирует процессор в контексте.
 *
 * Объектный URL нужен только на время компиляции модуля, поэтому
 * освобождается сразу — и при успехе, и при ошибке.
 */
export async function loadRecorderWorklet(
  context: BaseAudioContext,
): Promise<void> {
  const blob = new Blob([RECORDER_WORKLET_SOURCE], { type: WORKLET_MIME_TYPE });
  const url = URL.createObjectURL(blob);
  try {
    await context.audioWorklet.addModule(url);
  } finally {
    URL.revokeObjectURL(url);
  }
}
